/**
 * DuskTheme — DASH-05 dusk theme auto-engagement.
 *
 * Plan 01-14 Task 4. CR-03 fix: Plan 01-21.
 *
 * Headless effect host (returns null).  Toggles `html.theme-dusk` based on:
 *   1. Admin override (`/api/app-config/theme-mode`):
 *        - `'alltid mørk'` → forced ON
 *        - `'alltid lys'`  → forced OFF
 *        - `'auto'`        → time-of-day rule below
 *   2. Auto rule using /api/sun/today (Plan 07):
 *        - `now >= sunset_today` OR `now < sunrise_today` → ON
 *        - otherwise → OFF
 *      (Tomorrow's sunrise drift across midnight is ignorable for Phase 1;
 *      Phase 6 polish can fetch /api/sun/tomorrow.)
 *   3. Fallback path when sun.stale === true (Met.no unreachable per
 *      D-06): use fixed sunset 17:00 Europe/Oslo — `osloHour >= 17 ||
 *      osloHour < 7` → ON. sunrise_at is null on this path; only the
 *      synthesised sunset_at ISO string is present.
 *
 * Re-evaluates every 60s via setInterval so the transition fires on the
 * minute (Plan 09 dusk.css handles the 500ms cross-fade with
 * prefers-reduced-motion respected).
 *
 * Cleanup: removes `theme-dusk` from <html> on unmount so a Plan 17 e2e
 * spec can re-mount + re-evaluate from a clean baseline.
 *
 * #151: montert ved App-roten, så temaet gjelder også flytene utenfor
 * skallet. Holder også `<meta name="theme-color">` i takt med temaet, så
 * statuslinja i den installerte appen får lerretsfargen i begge temaer. En
 * `media="(prefers-color-scheme: …)"`-meta ville fulgt systemet, ikke
 * temavalget her.
 */
import { useEffect } from 'react';
import { useSunTimes, useThemeMode } from './useSunTimes';
import type { SunResponse } from './useSunTimes';
import { useThemePref } from './useThemePref';

function osloHour(now: Date): number {
  // Intl.DateTimeFormat handles CET/CEST DST without any tz data dep —
  // matches the Plan 07 sun.service approach.
  const hour = new Intl.DateTimeFormat('nb-NO', {
    timeZone: 'Europe/Oslo',
    hour: '2-digit',
    hour12: false,
  }).format(now);
  return Number(hour);
}

export function isDuskAuto(now: Date, sun: SunResponse): boolean {
  // CR-03 fix: stale flag is the D-06 fallback signal; sunrise_at may be
  // null on the fallback path, so the fallback branch uses pure clock math.
  if (sun.stale) {
    const h = osloHour(now);
    return h >= 17 || h < 7;
  }
  // Defensive: API contract admits null on either field. Treat null as
  // "data not yet available" → don't toggle dusk on this tick.
  if (!sun.sunset_at || !sun.sunrise_at) return false;
  const sunset = new Date(sun.sunset_at);
  const sunrise = new Date(sun.sunrise_at);
  // Invalid Date defence — comparing against an Invalid Date returns false,
  // so a malformed string would silently keep dusk off forever. Guard.
  if (Number.isNaN(sunset.getTime()) || Number.isNaN(sunrise.getTime())) return false;
  return now >= sunset || now < sunrise;
}

/**
 * Setter `theme-color` til lerretsfargen (`--color-bg`) for temaet som nå
 * gjelder. Verdien leses fra CSS-en, så den aldri glir fra tokenet; uten
 * stilark (jsdom) er den tom, og da står index.html sin lyse verdi.
 */
function syncThemeColor(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) return;
  const bg = getComputedStyle(document.documentElement).getPropertyValue('--color-bg').trim();
  if (bg) meta.content = bg;
}

export function DuskTheme() {
  const { data: sun } = useSunTimes();
  const { data: mode } = useThemeMode();
  const pref = useThemePref((s) => s.pref);

  useEffect(() => {
    const cls = document.documentElement.classList;

    const decide = () => {
      // Per-device user override wins over everything — anyone can force
      // light/dark from Settings without touching the household admin
      // config.  `'auto'` falls through to the admin + sun rule below.
      if (pref === 'dark') {
        cls.add('theme-dusk');
        return;
      }
      if (pref === 'light') {
        cls.remove('theme-dusk');
        return;
      }
      // Admin override branches next — supersedes auto rule per D-06.
      if (mode?.mode === 'alltid mørk') {
        cls.add('theme-dusk');
        return;
      }
      if (mode?.mode === 'alltid lys') {
        cls.remove('theme-dusk');
        return;
      }
      // Auto branch — needs sun data; if not yet loaded, leave as-is.
      if (!sun) return;
      if (isDuskAuto(new Date(), sun)) {
        cls.add('theme-dusk');
      } else {
        cls.remove('theme-dusk');
      }
    };

    const apply = () => {
      decide();
      syncThemeColor();
    };

    apply();
    const id = setInterval(apply, 60_000);
    return () => {
      clearInterval(id);
      cls.remove('theme-dusk');
      syncThemeColor();
    };
  }, [sun, mode, pref]);

  return null;
}
